import { useLayoutEffect, useRef, useState } from 'react'
import { Check, Circle, Minus, X } from 'lucide-react'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/ui/tooltip'
import { t } from '@/shared/lib/i18n'
import type { WebInstrumentCalendarView } from '@/shared/types/web'
import { getCellSymbol } from '@/shared/lib/calendar-cell-symbol'
import type { CellState as SymbolCellState } from '@/shared/lib/calendar-cell-symbol'
import { QA_ALERT_CATEGORY_MAP } from '@/shared/constants/qaAlertConfig'
import { STATUS_PALETTE } from '@/shared/constants/statusPalette'
import { hasNonTaskCalendarContent } from './hasNonTaskCalendarContent'
import {
  LOG_CLICKABLE_STATES,
  MISSED_CLICKABLE_STATES,
  isLogClickableCell,
  isMissedClickableCell,
} from './calendarCellClickability'
import { useSiteDateTime } from '@/shared/hooks/useSiteDateTime'

type TaskRowDataField = WebInstrumentCalendarView['taskRows'][number]['dataFields'][number]

const TASK_COLUMN_WIDTH_PX = 200
// Floor for a single date column. Date columns normally stretch to fill the container width so a
// full month lines up with the tabs header, but never shrink below this (keeps the grid readable
// and scrollable on narrow viewports, matching the pre-partial-selection minimum).
const DATE_COLUMN_MIN_WIDTH_PX = 28
// Shaves the table's total width so its right border is not clipped by the scroll container edge.
const TABLE_WIDTH_TRIM_PX = 5

/** Days in the given month (1-indexed month). */
const daysInMonth = (year: number, month: number): number => new Date(year, month, 0).getDate()

/**
 * Largest day-count among the months present in the rendered date columns. Used as the divisor for
 * the column width so that when a whole month is selected the table exactly spans the container
 * (no trailing blank space), while partial selections keep that same per-column width.
 */
const maxDaysInRenderedMonths = (dateColumns: string[]): number => {
  const seenMonths = new Set<string>()
  let maxDays = 0
  for (const isoDate of dateColumns) {
    const monthKey = isoDate.slice(0, 7)
    if (seenMonths.has(monthKey)) continue
    seenMonths.add(monthKey)
    const [year, month] = monthKey.split('-').map(Number)
    maxDays = Math.max(maxDays, daysInMonth(year, month))
  }
  return maxDays
}

interface Props {
  reportsCalendar: WebInstrumentCalendarView | null
  loading: boolean
  alertOnly: boolean
  showTaskFrequencyDetails: boolean
  onLogCellClick?: (params: {
    instrumentTaskId: number | null
    taskLogId: number
    taskName: string
    taskNotes: string | null
    date: string
    intervalTypeDescription: string | null
    dataFields: TaskRowDataField[]
    cellState: CellState
  }) => void
  onMissedCellClick?: (params: {
    instrumentTaskId: number
    requiredComplianceId: number
    taskName: string
    taskNotes: string | null
    date: string
    intervalTypeDescription: string | null
    cellState: 'failure' | 'override' | 'confirmed'
    dataFields: TaskRowDataField[]
  }) => void
  onExtraRowCellClick?: (params: { rowType: ExtraRowType; date: string }) => void
  onUnscheduledCellClick?: (params: { taskLogId: number; date: string }) => void
  onFailureRowCellClick?: (params: {
    date: string
    instrumentTaskLogIds: number[]
    // REBUILD-554: per-occurrence identity so the dialog can label/open the exact failure.
    failures: Array<{ id: number; completedAtLocal: string | null }>
  }) => void
  onTaskNameClick?: (taskRow: TaskRow) => void
}

export type ExtraRowType = 'serviceReports' | 'statusChanges' | 'departmentMoves'

type CellState = 'success' | 'failure' | 'no_testing' | 'pending' | 'override' | 'confirmed'

// Calendar cell fills — NextGen Status Color Standard §02. Fill carries the state; the glyph
// is black (color lives in the fill, never both). Derived from the core palette so a hue change
// propagates. Mapping: Complete → grey, No Testing → grey, Override → yellow, Confirmed NC → red.
// `failure` keeps a red fill here because the frontend cannot yet distinguish an unreviewed NC
// miss (red, must stay visible) from a failure-alone attempt — that split is the backend-ladder
// work (§02b). Complete + date change is yellow, resolved in cellBackgroundClass() below.
const CELL_BACKGROUND: Record<CellState, string> = {
  success: STATUS_PALETTE.grey.tintBg,
  failure: STATUS_PALETTE.red.tintBg,
  no_testing: STATUS_PALETTE.grey.tintBg,
  pending: '',
  override: STATUS_PALETTE.yellow.tintBg,
  confirmed: STATUS_PALETTE.red.tintBg,
}

// A completed task whose record was date-modified reads as "the record was altered" → yellow
// fill (§02 Complete + date change), overriding the plain grey Complete fill.
const cellBackgroundClass = (state: CellState, dateModified: boolean): string =>
  state === 'success' && dateModified ? STATUS_PALETTE.yellow.tintBg : CELL_BACKGROUND[state]

/**
 * Renders the icon for a calendar cell. Uses getCellSymbol as the single
 * source of truth for symbol→state mapping (no duplicate logic).
 *
 * Only `confirmed` renders an X badge; `override` and `no_testing` render O / dash.
 *
 * Accessibility (§02): color lives in the cell FILL (cellBackgroundClass), the glyph
 * is BLACK — never both. The single exception is the unreviewed-NC ✕, which stays
 * deep-red so it reads by lightness alone on the red tint for a colorblind reader.
 */
const CorrectiveActionMark = () => (
  <span
    className={`pointer-events-none absolute -right-0.5 -top-0.5 h-1.5 w-1.5 rounded-full ${QA_ALERT_CATEGORY_MAP.correctiveActions.dotBgColorClass}`}
    data-testid="corrective-action-mark"
    aria-hidden
  />
)

const CellIcon = ({
  state,
  dateModified,
  hasCorrectiveAction = false,
  reserveAsteriskOverhang = false,
}: {
  state: CellState
  dateModified: boolean
  hasCorrectiveAction?: boolean
  /** Mobile accordion only — legend clearance beside the status label. Desktop omits so the check stays centered (AC6). */
  reserveAsteriskOverhang?: boolean
}) => {
  const glyph = cellGlyph(state, dateModified, reserveAsteriskOverhang)
  if (!hasCorrectiveAction) return glyph

  return (
    <span className="relative inline-flex">
      {glyph}
      <CorrectiveActionMark />
    </span>
  )
}

const cellGlyph = (state: CellState, dateModified: boolean, reserveAsteriskOverhang: boolean) => {
  const symbolDef = getCellSymbol(state as SymbolCellState, { dateModified })

  switch (symbolDef.kind) {
    case 'check':
      return <Check className="h-3.5 w-3.5 text-black" />
    case 'check_date_changed':
      // §02 Complete (date changed): ✓* on the yellow fill. The asterisk is absolutely
      // positioned so column widths do not change (same pattern as confirmed x_badge). Desktop
      // has no mr so the check stays centered; mobile reserves overhang beside the row label.
      return (
        <span
          className={['relative inline-flex text-black', reserveAsteriskOverhang ? 'mr-1.5' : '']
            .filter(Boolean)
            .join(' ')}
        >
          <Check className="h-3.5 w-3.5 text-black" />
          <span
            className="absolute -right-1.5 -top-1 text-[10px] font-bold leading-none"
            aria-hidden
          >
            *
          </span>
        </span>
      )
    case 'x':
      // The one black-glyph exception (§02): unreviewed NC ✕ stays deep-red so it reads by
      // lightness alone on the red tint, even for a colorblind reader.
      return <X className={`h-3.5 w-3.5 ${STATUS_PALETTE.red.deepText}`} />
    case 'x_badge':
      // Confirmed NC: black ✕ + filled "!" badge (decided 24 Sep — matches the app today).
      return (
        <span className="relative">
          <X className="h-3.5 w-3.5 text-black" />
          <span className="absolute -right-1.5 -top-1.5 flex h-2.5 w-2.5 items-center justify-center rounded-full bg-black text-[7px] font-bold text-white leading-none">
            !
          </span>
        </span>
      )
    case 'dash':
      return <Minus className="h-3.5 w-3.5 text-black" />
    case 'letter_o':
      return (
        <span className="text-xs font-bold text-black leading-none" aria-hidden>
          O
        </span>
      )
    default:
      return null
  }
}

const dayLabel = (isoDate: string) => {
  const day = Number(isoDate.split('-')[2])
  return String(day).padStart(2, '0')
}

const isToday = (isoDate: string, siteToday: string): boolean => isoDate === siteToday

type TaskRow = WebInstrumentCalendarView['taskRows'][number]
type Cell = TaskRow['cells'][number]

function buildCellAriaLabel(
  state: CellState,
  taskName: string,
  date: string,
  hasCorrectiveAction = false,
): string | undefined {
  let label: string | undefined
  if (state === 'success') {
    label = t('instruments.reports.cell.successAria', { task: taskName, date })
  } else if (state === 'no_testing') {
    label = t('instruments.reports.cell.nptAria', { task: taskName, date })
  } else if (state === 'failure' || state === 'override' || state === 'confirmed') {
    label = t('instruments.reports.cell.missedAria', { task: taskName, date })
  }
  if (!label || !hasCorrectiveAction) return label

  return `${label}, ${t(QA_ALERT_CATEGORY_MAP.correctiveActions.labelKey)}`
}

export const CalendarGrid = ({
  reportsCalendar,
  loading,
  alertOnly,
  showTaskFrequencyDetails,
  onLogCellClick,
  onMissedCellClick,
  onExtraRowCellClick,
  onUnscheduledCellClick,
  onFailureRowCellClick,
  onTaskNameClick,
}: Props) => {
  const { formatWallClockDisplay } = useSiteDateTime()

  // Measure the scroll container so date columns can stretch to fill its width (see
  // dateColumnWidth below). Falls back to the fixed minimum until the container is measured
  // (e.g. under jsdom, where the ResizeObserver stub never fires).
  const tableContainerRef = useRef<HTMLDivElement>(null)
  const [containerWidth, setContainerWidth] = useState(0)

  useLayoutEffect(() => {
    const container = tableContainerRef.current
    if (!container) return
    const measure = () => setContainerWidth(container.clientWidth)
    measure()
    const resizeObserver = new ResizeObserver(measure)
    resizeObserver.observe(container)
    return () => resizeObserver.disconnect()
  }, [reportsCalendar])

  if (reportsCalendar === null) {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-8">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      )
    }
    return (
      <div className="py-8 text-center text-sm text-muted-foreground">
        {t('instruments.show.reports.empty')}
      </div>
    )
  }

  const {
    dateColumns,
    taskRows,
    failureRow,
    serviceReportRow,
    statusChangesRow,
    departmentMovedRow,
    unscheduledRow,
    siteToday,
    defaultFilters,
  } = reportsCalendar

  // Non-task rows (Failure / Status Changes / Unscheduled / Service Report / Department Moved)
  // are built by the calendar service even when taskRows is empty. Only show the empty copy when
  // those rows also have nothing to display (test anchor: calendarEmptyCopy.test.mjs).
  if (taskRows.length === 0 && !hasNonTaskCalendarContent(reportsCalendar)) {
    const emptyMessageKey =
      defaultFilters.taskIds.length > 0
        ? 'instruments.show.reports.emptyFilter'
        : 'instruments.show.reports.empty'
    return (
      <div className="py-8 text-center text-sm text-muted-foreground">{t(emptyMessageKey)}</div>
    )
  }

  // Alert-only filtering: hide success cells (show as blank); hide rows with zero non-success cells
  const visibleTaskRows = alertOnly
    ? taskRows.filter((row) =>
        row.cells.some((cell) => cell.state !== 'success' && cell.state !== 'pending'),
      )
    : taskRows

  const handleLogClick = (cell: Cell, taskRow: TaskRow) => {
    if (!onLogCellClick) return
    const state = cell.state as CellState
    const isExecFail = state === 'failure' && cell.meta.instrumentTaskLogId != null
    if ((!LOG_CLICKABLE_STATES.has(state) && !isExecFail) || cell.meta.instrumentTaskLogId == null)
      return
    onLogCellClick({
      instrumentTaskId: taskRow.instrumentTaskId,
      taskLogId: cell.meta.instrumentTaskLogId,
      taskName: taskRow.taskName,
      taskNotes: taskRow.taskNotes,
      date: cell.date,
      intervalTypeDescription: taskRow.intervalTypeDescription,
      dataFields: taskRow.dataFields ?? [],
      cellState: state,
    })
  }

  const handleMissedClick = (cell: Cell, taskRow: TaskRow) => {
    if (!onMissedCellClick) return
    const state = cell.state as CellState
    if (!MISSED_CLICKABLE_STATES.has(state) || cell.meta.requiredComplianceId == null) return
    onMissedCellClick({
      instrumentTaskId: taskRow.instrumentTaskId,
      requiredComplianceId: cell.meta.requiredComplianceId,
      taskName: taskRow.taskName,
      taskNotes: taskRow.taskNotes,
      date: cell.date,
      intervalTypeDescription: taskRow.intervalTypeDescription,
      cellState: state as 'failure' | 'override' | 'confirmed',
      dataFields: taskRow.dataFields ?? [],
    })
  }

  const renderCell = (cell: Cell, taskRow: TaskRow) => {
    const state = cell.state as CellState

    if (cell.meta.dayOff && state === 'pending') {
      return (
        <TableCell key={cell.date} className="bg-muted/30 p-0 text-center">
          <div className="flex h-9 w-full items-center justify-center">
            <span className="text-[9px] font-medium text-muted-foreground leading-none">
              {t('instruments.reports.cell.dayOff')}
            </span>
          </div>
        </TableCell>
      )
    }

    // Alert-only: show success cells as blank
    const displayState: CellState = alertOnly && state === 'success' ? 'pending' : state

    // Routing rules live in calendarCellClickability (shared with the Reports list view).
    // Calendar service must not attach same-day fallback logs (QC-fail or success) onto true-miss cells.
    const isLogClickable = isLogClickableCell(cell)
    const isMissedClickable = isMissedClickableCell(cell)
    const isClickable = isLogClickable || isMissedClickable
    const ariaLabel = buildCellAriaLabel(
      displayState,
      taskRow.taskName,
      cell.date,
      cell.meta.hasCorrectiveAction === true,
    )

    // Tooltip: action label for non-pending cells (AC #7), enriched with completion
    // attribution (who + when) surfaced by the calendar service (E35 parity).
    const symbolDef = getCellSymbol(displayState as SymbolCellState)
    // REBUILD-474: completed/reviewed instants use MM/DD/YYYY, HH:mm (not long-month).
    const completedAtLabel = cell.meta.completedAtLocal
      ? formatWallClockDisplay(cell.meta.completedAtLocal)
      : null
    // REBUILD-192: a backdated success cell uses the existing (date-modified)
    // convention, not "Complete", on the calendar status tooltip.
    const isDateModifiedSuccess = displayState === 'success' && cell.meta.dateModified
    const isConfirmed = displayState === 'confirmed'
    // Reviewed misses (override + confirmed) have no execution log; their actor/time come
    // from the review_* meta (who reviewed the miss and when), not the completion fields.
    const isReviewed = isConfirmed || displayState === 'override'
    const statusLabel = isDateModifiedSuccess
      ? t('instruments.reports.cell.dateModifiedLabel')
      : isConfirmed
        ? t('instruments.reports.cell.confirmedTooltip')
        : symbolDef.label
    const tooltipActor = isReviewed ? cell.meta.reviewActorName : cell.meta.completedByName
    const tooltipDateTime = isReviewed
      ? cell.meta.reviewRecordedAtLocal
        ? formatWallClockDisplay(cell.meta.reviewRecordedAtLocal)
        : null
      : completedAtLabel
    const cellTooltip =
      displayState !== 'pending' && statusLabel
        ? [statusLabel, tooltipActor, tooltipDateTime].filter(Boolean).join('\n')
        : undefined

    const handleClick = () => {
      if (isLogClickable) handleLogClick(cell, taskRow)
      else if (isMissedClickable) handleMissedClick(cell, taskRow)
    }

    return (
      <TableCell
        key={cell.date}
        className={[
          'p-0 text-center',
          cellBackgroundClass(displayState, cell.meta.dateModified),
          isClickable ? 'cursor-pointer' : 'cursor-default',
        ]
          .filter(Boolean)
          .join(' ')}
        onClick={isClickable ? handleClick : undefined}
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              className={[
                'flex h-9 w-full items-center justify-center',
                isClickable ? 'hover:ring-2 hover:ring-inset hover:ring-ring' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              {...(ariaLabel ? { role: 'button', 'aria-label': ariaLabel, tabIndex: 0 } : {})}
              onKeyDown={
                isClickable
                  ? (keyEvent) => {
                      if (keyEvent.key === 'Enter' || keyEvent.key === ' ') {
                        keyEvent.preventDefault()
                        handleClick()
                      }
                    }
                  : undefined
              }
            >
              {displayState !== 'pending' && (
                <CellIcon
                  state={displayState}
                  dateModified={cell.meta.dateModified}
                  hasCorrectiveAction={cell.meta.hasCorrectiveAction === true}
                />
              )}
            </div>
          </TooltipTrigger>
          {cellTooltip && (
            <TooltipContent className="whitespace-pre-line">{cellTooltip}</TooltipContent>
          )}
        </Tooltip>
      </TableCell>
    )
  }

  // Stretch date columns to fill the container: divide the space left of the task column by the
  // month's day count so a full month aligns with the tabs header. Partial selections keep the
  // same per-column width (table stays narrower than the container). Never go below the minimum.
  const dividerDayCount = maxDaysInRenderedMonths(dateColumns)
  // Trim a few px from the space divided among date columns so the table's right border stays
  // inside the scroll container (otherwise it sits flush against the edge and gets clipped).
  // Trimming here (not the table width) matters because table-fixed lets the column widths, not
  // the table's own width, determine the total.
  const availableDateWidth = containerWidth - TASK_COLUMN_WIDTH_PX - TABLE_WIDTH_TRIM_PX
  const dateColumnWidth =
    containerWidth > 0 && dividerDayCount > 0
      ? Math.max(DATE_COLUMN_MIN_WIDTH_PX, availableDateWidth / dividerDayCount)
      : DATE_COLUMN_MIN_WIDTH_PX
  const tableWidth = TASK_COLUMN_WIDTH_PX + dateColumns.length * dateColumnWidth

  return (
    <div className="relative">
      {loading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center rounded bg-background/60">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      )}

      {/* Desktop table */}
      <div
        ref={tableContainerRef}
        className={`hidden sm:block min-w-0 overflow-x-auto ${loading ? 'pointer-events-none opacity-60' : ''}`}
      >
        <Table
          className="table-fixed border-collapse border border-border [&_td]:border [&_td]:border-border [&_th]:border [&_th]:border-border"
          style={{ width: tableWidth }}
        >
          <TableHeader>
            <TableRow>
              <TableHead className="sticky left-0 z-10 w-[200px] bg-background text-left">
                {t('instruments.show.reports.grid.taskColumn')}
              </TableHead>
              {dateColumns.map((date) => (
                <TableHead
                  key={date}
                  style={{ width: dateColumnWidth }}
                  className={[
                    'px-0.5 text-center text-xs',
                    'font-normal',
                    isToday(date, siteToday) ? 'font-bold text-primary' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  {dayLabel(date)}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {visibleTaskRows.map((taskRow) => (
              <TableRow key={taskRow.instrumentTaskId}>
                <TableCell
                  className="sticky left-0 z-10 bg-background py-0.5 pr-2"
                  title={taskRow.taskName}
                >
                  <button
                    type="button"
                    className={[
                      'block max-w-[200px] truncate text-left text-xs font-medium',
                      onTaskNameClick ? 'cursor-pointer underline-offset-2 hover:underline' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={onTaskNameClick ? () => onTaskNameClick(taskRow) : undefined}
                  >
                    {taskRow.taskName}
                    {taskRow.taskInactive && (
                      <span className="ml-1 text-[10px] font-normal text-muted-foreground">
                        {t('instruments.reports.cell.inactiveTaskSuffix')}
                      </span>
                    )}
                  </button>
                  {showTaskFrequencyDetails && taskRow.intervalTypeDescription && (
                    <span className="text-[10px] text-muted-foreground">
                      {taskRow.intervalTypeDescription}
                    </span>
                  )}
                </TableCell>
                {taskRow.cells.map((cell) => renderCell(cell, taskRow))}
              </TableRow>
            ))}

            {/* Extra rows — event markers with colored dots */}
            <ExtraEventRow
              label={t('instruments.reports.extraRows.serviceReports')}
              row={serviceReportRow}
              dateColumns={dateColumns}
              dotColor={QA_ALERT_CATEGORY_MAP.serviceReports.dotColorClass}
              onCellClick={
                onExtraRowCellClick
                  ? (date) => onExtraRowCellClick({ rowType: 'serviceReports', date })
                  : undefined
              }
            />
            <ExtraEventRow
              label={t('instruments.reports.extraRows.unscheduled')}
              row={unscheduledRow}
              dateColumns={dateColumns}
              // No qaAlertConfig entry — unscheduled is calendar-only (matches CalendarLegend).
              dotColor="text-purple-500"
              onCellClick={
                onUnscheduledCellClick
                  ? (date) => {
                      const cell = unscheduledRow.cells.find(
                        (unscheduledCell) => unscheduledCell.date === date,
                      )
                      if (cell && 'firstLogId' in cell && cell.firstLogId != null) {
                        onUnscheduledCellClick({ taskLogId: cell.firstLogId, date })
                      }
                    }
                  : undefined
              }
            />
            <ExtraEventRow
              label={t('instruments.reports.extraRows.statusChanges')}
              row={statusChangesRow}
              dateColumns={dateColumns}
              dotColor={QA_ALERT_CATEGORY_MAP.statusChanges.dotColorClass}
              onCellClick={
                onExtraRowCellClick
                  ? (date) => onExtraRowCellClick({ rowType: 'statusChanges', date })
                  : undefined
              }
            />
            <ExtraEventRow
              label={t('instruments.reports.extraRows.departmentMoved')}
              row={departmentMovedRow}
              dateColumns={dateColumns}
              dotColor={QA_ALERT_CATEGORY_MAP.departmentMoved.dotColorClass}
              onCellClick={
                onExtraRowCellClick
                  ? (date) => onExtraRowCellClick({ rowType: 'departmentMoves', date })
                  : undefined
              }
            />

            {/* Failure aggregation row — only rendered when at least one cell has failures (test anchor: failureRowConditional.test.mjs).
                Neutral bg + amber count text matches ExtraEventRow siblings; category identity is qaAlertConfig count color, not row fill. */}
            {failureRow.cells.some((cell) => cell.failureCount > 0) && (
              <TableRow className="border-t-2 bg-muted/10">
                <TableCell className="sticky left-0 z-10 bg-muted/10 py-1 pr-2 text-xs font-semibold text-muted-foreground">
                  {t('instruments.show.reports.grid.failureRow')}
                </TableCell>
                {failureRow.cells.map((cell) => {
                  const count = cell.failureCount
                  const logIds = cell.instrumentTaskLogIds
                  const failures = cell.failures ?? []
                  const isClickable = count > 0 && onFailureRowCellClick != null
                  return (
                    <TableCell
                      key={cell.date}
                      className={['p-0 text-center', isClickable ? 'cursor-pointer' : '']
                        .filter(Boolean)
                        .join(' ')}
                      onClick={
                        isClickable
                          ? () =>
                              onFailureRowCellClick({
                                date: cell.date,
                                instrumentTaskLogIds: logIds,
                                failures,
                              })
                          : undefined
                      }
                    >
                      <div
                        className={[
                          'flex h-9 w-full items-center justify-center',
                          isClickable ? 'hover:ring-2 hover:ring-inset hover:ring-ring' : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        {...(count > 0
                          ? {
                              role: 'button',
                              'aria-label': t('instruments.reports.failures.aria', {
                                date: cell.date,
                                count: String(count),
                              }),
                              tabIndex: 0,
                            }
                          : {})}
                        onKeyDown={
                          isClickable
                            ? (keyEvent) => {
                                if (keyEvent.key === 'Enter' || keyEvent.key === ' ') {
                                  keyEvent.preventDefault()
                                  onFailureRowCellClick({
                                    date: cell.date,
                                    instrumentTaskLogIds: logIds,
                                    failures,
                                  })
                                }
                              }
                            : undefined
                        }
                      >
                        {count > 0 ? (
                          <span
                            className={`text-xs font-medium ${QA_ALERT_CATEGORY_MAP.failures.dotColorClass}`}
                          >
                            {count}
                          </span>
                        ) : null}
                      </div>
                    </TableCell>
                  )
                })}
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Mobile accordion */}
      <div className={`sm:hidden space-y-2 ${loading ? 'pointer-events-none opacity-60' : ''}`}>
        {visibleTaskRows.map((taskRow) => (
          <MobileTaskAccordion
            key={taskRow.instrumentTaskId}
            taskRow={taskRow}
            alertOnly={alertOnly}
            onLogCellClick={onLogCellClick}
            onMissedCellClick={onMissedCellClick}
          />
        ))}

        {/* Extra rows — event summaries */}
        <MobileExtraRow
          label={t('instruments.reports.extraRows.serviceReports')}
          row={serviceReportRow}
          dotColor={QA_ALERT_CATEGORY_MAP.serviceReports.dotColorClass}
        />
        <MobileExtraRow
          label={t('instruments.reports.extraRows.unscheduled')}
          row={unscheduledRow}
          // No qaAlertConfig entry — unscheduled is calendar-only (matches CalendarLegend).
          dotColor="text-purple-500"
        />
        <MobileExtraRow
          label={t('instruments.reports.extraRows.statusChanges')}
          row={statusChangesRow}
          dotColor={QA_ALERT_CATEGORY_MAP.statusChanges.dotColorClass}
        />
        <MobileExtraRow
          label={t('instruments.reports.extraRows.departmentMoved')}
          row={departmentMovedRow}
          dotColor={QA_ALERT_CATEGORY_MAP.departmentMoved.dotColorClass}
        />

        {/* Failure summary — only shown when at least one cell has failures (test anchor: failureRowConditional.test.mjs) */}
        {failureRow.cells.some((cell) => cell.failureCount > 0) && (
          <div className="rounded border p-3">
            <p className="mb-2 text-xs font-semibold text-muted-foreground">
              {t('instruments.show.reports.grid.failureRow')}
            </p>
            <div className="space-y-0.5">
              {failureRow.cells
                .filter((cell) => cell.failureCount > 0)
                .map((cell) => {
                  const failures = cell.failures ?? []
                  const isClickable = onFailureRowCellClick != null
                  return (
                    <div
                      key={cell.date}
                      className={[
                        'flex items-center justify-between text-xs rounded px-1 -mx-1',
                        isClickable ? 'cursor-pointer hover:bg-muted/50' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      role={isClickable ? 'button' : undefined}
                      tabIndex={isClickable ? 0 : undefined}
                      aria-label={
                        isClickable
                          ? t('instruments.reports.failures.aria', {
                              date: cell.date,
                              count: String(cell.failureCount),
                            })
                          : undefined
                      }
                      onClick={
                        isClickable
                          ? () =>
                              onFailureRowCellClick({
                                date: cell.date,
                                instrumentTaskLogIds: cell.instrumentTaskLogIds,
                                failures,
                              })
                          : undefined
                      }
                      onKeyDown={
                        isClickable
                          ? (keyEvent) => {
                              if (keyEvent.key === 'Enter' || keyEvent.key === ' ') {
                                keyEvent.preventDefault()
                                onFailureRowCellClick({
                                  date: cell.date,
                                  instrumentTaskLogIds: cell.instrumentTaskLogIds,
                                  failures,
                                })
                              }
                            }
                          : undefined
                      }
                    >
                      <span className="text-muted-foreground">{cell.date}</span>
                      <span
                        className={`font-medium ${QA_ALERT_CATEGORY_MAP.failures.dotColorClass}`}
                      >
                        {cell.failureCount}
                      </span>
                    </div>
                  )
                })}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ── ExtraEventRow ─────────────────────────────────────────────────────────────

interface ExtraEventRowProps {
  label: string
  row: WebInstrumentCalendarView['serviceReportRow'] | WebInstrumentCalendarView['unscheduledRow']
  dateColumns: string[]
  dotColor: string
  onCellClick?: (date: string) => void
}

const ExtraEventRow = ({ label, row, dateColumns, dotColor, onCellClick }: ExtraEventRowProps) => {
  const hasAnyEvent = row.cells.some((cell) => cell.eventCount > 0)
  if (!hasAnyEvent) return null

  return (
    <TableRow className="bg-muted/10">
      <TableCell className="sticky left-0 z-10 bg-muted/10 py-1 pr-2 text-xs text-muted-foreground">
        {label}
      </TableCell>
      {dateColumns.map((date) => {
        const cell = row.cells.find((rowCell) => rowCell.date === date)
        const count = cell?.eventCount ?? 0
        const isClickable = count > 0 && onCellClick != null
        return (
          <TableCell
            key={date}
            className={['p-0 text-center', isClickable ? 'cursor-pointer' : '']
              .filter(Boolean)
              .join(' ')}
            onClick={isClickable ? () => onCellClick(date) : undefined}
          >
            <div
              className={[
                'flex h-9 w-full items-center justify-center',
                isClickable ? 'hover:ring-2 hover:ring-inset hover:ring-ring' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              {...(count > 0
                ? {
                    role: 'button',
                    'aria-label': `${count} event${count !== 1 ? 's' : ''}`,
                    title: `${count} event${count !== 1 ? 's' : ''}`,
                  }
                : {})}
            >
              {count > 0 ? (
                <span className={`text-xs font-medium ${dotColor}`}>{count}</span>
              ) : null}
            </div>
          </TableCell>
        )
      })}
    </TableRow>
  )
}

// ── MobileExtraRow ────────────────────────────────────────────────────────────

interface MobileExtraRowProps {
  label: string
  row: WebInstrumentCalendarView['serviceReportRow']
  dotColor: string
}

const MobileExtraRow = ({ label, row, dotColor }: MobileExtraRowProps) => {
  const datesWithEvents = row.cells.filter((cell) => cell.eventCount > 0)
  if (datesWithEvents.length === 0) return null

  return (
    <div className="rounded border p-3">
      <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
        <Circle className={`h-2.5 w-2.5 fill-current ${dotColor}`} />
        {label}
      </p>
      <div className="space-y-0.5">
        {datesWithEvents.map((cell) => (
          <div key={cell.date} className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">{cell.date}</span>
            <span
              className="font-medium"
              role="img"
              aria-label={`${cell.eventCount} event${cell.eventCount !== 1 ? 's' : ''}`}
            >
              {cell.eventCount}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── MobileTaskAccordion ───────────────────────────────────────────────────────

interface MobileTaskAccordionProps {
  taskRow: TaskRow
  alertOnly: boolean
  onLogCellClick?: Props['onLogCellClick']
  onMissedCellClick?: Props['onMissedCellClick']
}

const MobileTaskAccordion = ({
  taskRow,
  alertOnly,
  onLogCellClick,
  onMissedCellClick,
}: MobileTaskAccordionProps) => {
  const notableEntries = taskRow.cells.filter((cell) => {
    if (cell.state === 'pending') return false
    if (alertOnly && cell.state === 'success') return false
    return true
  })

  return (
    <details className="rounded border">
      <summary className="flex cursor-pointer items-center justify-between px-3 py-2 text-sm font-medium">
        <span className="line-clamp-1">
          {taskRow.taskName}
          {taskRow.taskInactive && (
            <span className="ml-1 text-xs font-normal text-muted-foreground">
              {t('instruments.reports.cell.inactiveTaskSuffix')}
            </span>
          )}
        </span>
        {notableEntries.length > 0 && (
          <span className="ml-2 shrink-0 rounded-full bg-red-50 px-1.5 py-0.5 text-[10px] font-semibold text-red-600">
            {notableEntries.length}
          </span>
        )}
      </summary>
      <div className="border-t px-3 py-2">
        {notableEntries.length === 0 ? (
          <p className="text-xs text-muted-foreground">—</p>
        ) : (
          <div className="space-y-0.5">
            {notableEntries.map((cell) => {
              const state = cell.state as CellState
              // Same routing as grid cells: log id on failure → Log Details; RC without
              // log id → day-occurrences (same-day fallback logs must not attach onto true miss).
              const isLogClickable = isLogClickableCell(cell)
              const isMissedClickable = isMissedClickableCell(cell)
              const isClickable = isLogClickable || isMissedClickable
              // REBUILD-227: a backdated/date-modified success cell must show ONE combined
              // status, not a base state plus a separate "(date-modified)" qualifier. Mirror
              // the desktop tooltip's single dateModifiedLabel for that case.
              const isDateModifiedSuccess = state === 'success' && cell.meta.dateModified
              const mobileStatusLabel = isDateModifiedSuccess
                ? t('instruments.reports.cell.dateModifiedLabel')
                : cell.state.replace('_', ' ')
              const cellAriaLabel = buildCellAriaLabel(
                state,
                taskRow.taskName,
                cell.date,
                cell.meta.hasCorrectiveAction === true,
              )
              return (
                <div
                  key={cell.date}
                  role={cellAriaLabel ? 'group' : undefined}
                  aria-label={cellAriaLabel}
                  className={[
                    'flex items-center justify-between text-xs',
                    isClickable ? 'cursor-pointer hover:bg-muted/50 rounded px-1 -mx-1' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => {
                    if (isLogClickable && onLogCellClick) {
                      onLogCellClick({
                        instrumentTaskId: taskRow.instrumentTaskId,
                        taskLogId: cell.meta.instrumentTaskLogId!,
                        taskName: taskRow.taskName,
                        taskNotes: taskRow.taskNotes,
                        date: cell.date,
                        intervalTypeDescription: taskRow.intervalTypeDescription,
                        dataFields: taskRow.dataFields ?? [],
                        cellState: state,
                      })
                    } else if (isMissedClickable && onMissedCellClick) {
                      onMissedCellClick({
                        instrumentTaskId: taskRow.instrumentTaskId,
                        requiredComplianceId: cell.meta.requiredComplianceId!,
                        taskName: taskRow.taskName,
                        taskNotes: taskRow.taskNotes,
                        date: cell.date,
                        intervalTypeDescription: taskRow.intervalTypeDescription,
                        cellState: state as 'failure' | 'override' | 'confirmed',
                        dataFields: taskRow.dataFields ?? [],
                      })
                    }
                  }}
                >
                  <span className="text-muted-foreground">{cell.date}</span>
                  <div className="flex items-center gap-1">
                    <CellIcon
                      state={state}
                      dateModified={cell.meta.dateModified}
                      hasCorrectiveAction={cell.meta.hasCorrectiveAction === true}
                      reserveAsteriskOverhang
                    />
                    <span className="capitalize text-muted-foreground">{mobileStatusLabel}</span>
                    {cell.meta.completedByName && (
                      <span className="text-muted-foreground">— {cell.meta.completedByName}</span>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </details>
  )
}
